import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { isCustomerUser } from '@/integrations/supabase/session';
import { formatDocDate, formatMoney, resolveCompanyLocale } from '@/utils/locale';
import { PortalContext, type PortalContextValue } from './PortalContext';
import { usePortalCompany } from './hooks/usePortalCompany';
import { usePortalMe } from './hooks/usePortalMe';
import { rememberPortalSlug } from './returnTo';
import { PortalShell } from './components/PortalShell';
import { PortalLoading } from './components/PortalLoading';
import { PortalErrorState } from './components/PortalErrorState';
import PortalStaffBlocked from './pages/PortalStaffBlocked';

/**
 * Layout route for every signed-in portal page. Resolves the session, the
 * company behind the slug and the customer card this login is linked to,
 * then provides PortalContext and renders the shell around the page.
 */
export function CustomerRoute() {
  const { slug = '' } = useParams<{ slug: string }>();
  const location = useLocation();
  const { user, isLoading: authLoading, signOut } = useAuth();
  const isCustomer = !!user && isCustomerUser(user);

  const companyQuery = usePortalCompany(isCustomer ? slug : undefined);
  const company = companyQuery.data ?? null;
  const meQuery = usePortalMe(isCustomer && company ? company.id : undefined);
  const me = meQuery.data ?? null;

  useEffect(() => {
    if (company && me) rememberPortalSlug(slug);
  }, [company, me, slug]);

  const value = useMemo<PortalContextValue | undefined>(() => {
    if (!company || !me) return undefined;
    const locale = resolveCompanyLocale(company.country);
    const timezone = company.timezone || locale.defaultTimezone;
    const brand = company.primary_color && /^#[0-9a-f]{6}$/i.test(company.primary_color)
      ? company.primary_color
      : 'hsl(var(--primary))';
    return {
      slug,
      company,
      me,
      locale,
      timezone,
      formatMoney: (amount) => formatMoney(amount, locale),
      formatDate: (v) => formatDocDate(v, locale.longDate, timezone),
      formatShortDate: (v) => formatDocDate(v, locale.shortDate, timezone),
      primaryColor: brand,
    };
  }, [company, me, slug]);

  const loginPath = `/portal/${slug}`;

  if (authLoading) return <PortalLoading fullScreen label="Checking your sign-in…" />;
  if (!user) return <Navigate to={loginPath} replace state={{ from: location.pathname }} />;
  if (!isCustomer) return <PortalStaffBlocked />;

  if (companyQuery.isLoading || (company && meQuery.isLoading)) {
    return <PortalLoading fullScreen label="Loading your account…" />;
  }

  if (companyQuery.error || meQuery.error) {
    return (
      <PortalErrorState
        fullScreen
        onRetry={() => { void companyQuery.refetch(); void meQuery.refetch(); }}
        onSignIn={() => void signOut({ redirectTo: loginPath })}
      />
    );
  }

  if (!company || !me || !value) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <UserX className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h1 className="mb-2 text-lg font-semibold">This account isn't linked here</h1>
            <p className="text-sm text-muted-foreground">
              The email you signed in with isn't linked to this company's customer portal.
              Ask them to send you a fresh invitation, or sign in with the address they have on file.
            </p>
            <Button className="mt-6" variant="outline" onClick={() => void signOut({ redirectTo: loginPath })}>
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <PortalContext.Provider value={value}>
      <PortalShell>
        <Outlet />
      </PortalShell>
    </PortalContext.Provider>
  );
}
